import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import HealthTestCard from '@/components/HealthTestCard';
import { ArrowLeft, Award, Calendar, Weight, FileText, Camera, Plus, Trash2, Loader2, Image, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/contexts/AuthContext';

const DogProfile = () => {
  const { dogId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dog, setDog] = useState(null);
  const [owner, setOwner] = useState(null);
  const [healthRecords, setHealthRecords] = useState([]);
  const [pedigreeRows, setPedigreeRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [verificationError, setVerificationError] = useState(null);
  const [pedigreeId, setPedigreeId] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dogPhotos, setDogPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const profilePhotoInputRef = useRef(null);
  const galleryPhotoInputRef = useRef(null);
  
  useEffect(() => {
    fetchDogData();
  }, [dogId]);
  
  const fetchDogData = async () => {
    try {
      setLoading(true);
      
      // Fetch dog data
      const { data: dogData, error: dogError } = await supabase
        .from('dogs')
        .select('*')
        .eq('id', dogId)
        .single();
      
      if (dogError) throw dogError;
      setDog(dogData);
      
      // Fetch owner data
      const { data: ownerData, error: ownerError } = await supabase
        .from('users')
        .select('*')
        .eq('id', dogData.owner_id)
        .single();
      
      if (ownerError) throw ownerError;
      setOwner(ownerData);
      
      // Fetch health records
      const { data: healthData, error: healthError } = await supabase
        .from('health_records')
        .select('*')
        .eq('dog_id', dogId)
        .order('test_date', { ascending: false });
      
      if (healthError) throw healthError;
      setHealthRecords(healthData || []);
      
      // Fetch pedigree — returns array (multiple ancestor rows possible)
      const { data: pedigreeData } = await supabase
        .from('pedigrees')
        .select('*')
        .eq('dog_id', dogId);

      setPedigreeRows(pedigreeData || []);

      // Fetch gallery photos
      const { data: photos } = await supabase
        .from('dog_photos')
        .select('*')
        .eq('dog_id', dogId)
        .order('sort_order', { ascending: true });

      if (photos) setDogPhotos(photos);
      
    } catch (error) {
      console.error('Error fetching dog data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = new Date(dob);
    const today = new Date();
    const years = today.getFullYear() - birthDate.getFullYear();
    const months = today.getMonth() - birthDate.getMonth();
    
    if (years > 0) {
      return `${years} year${years > 1 ? 's' : ''}`;
    }
    return `${months} month${months > 1 ? 's' : ''}`;
  };
  
  const getTrustScoreBadge = (score) => {
    if (!score) return null;
    if (score >= 80) return { text: 'Gold Badge', color: '#C5A55A' };
    if (score >= 60) return { text: 'Silver Badge', color: '#94A3B8' };
    return null;
  };
  
  // Convert health records to the format expected by HealthTestCard
  const formatHealthTests = () => {
    const testTypes = ['OFA Hips', 'OFA Elbows', 'Cardiac', 'Eyes', 'DNA Panel', 'Patella'];
    
    return testTypes.map(testType => {
      const record = healthRecords.find(r => r.test_type === testType);
      
      if (record) {
        return {
          name: record.test_type,
          status: record.verified ? 'verified' : 'pending',
          result: record.result,
          date: record.test_date
        };
      }
      
      return {
        name: testType,
        status: 'missing',
        result: 'Not Done',
        date: null
      };
    });
  };
  
  const handleVerifyPedigree = async () => {
    setVerifying(true);
    setVerificationError(null);
    setVerificationResult(null);
    try {
      if (!pedigreeId.trim()) {
        setVerificationError('Please enter a pedigree database ID.');
        setVerifying(false);
        return;
      }
      const response = await fetch(
        `https://blubloodz-scraper.vercel.app/api/verify-pedigree?id=${encodeURIComponent(pedigreeId.trim())}`
      );
      const data = await response.json();
      if (data.success && data.data) {
        setVerificationResult(data.data);
      } else {
        setVerificationError('Dog not found. Check the ID and try again.');
      }
    } catch (err) {
      setVerificationError('Verification service unavailable. Please try again later.');
    }
    setVerifying(false);
  };

  const handleSaveVerification = async () => {
    if (!verificationResult || !dog) return;
    setSaving(true);
    setVerificationError(null);
    try {
      const { error: pedigreeError } = await supabase.from('pedigrees').insert({
        dog_id: dog.id,
        sire_name: verificationResult.sire?.name || null,
        dam_name: verificationResult.dam?.name || null,
        lineage: {
          sire: verificationResult.sire || null,
          dam: verificationResult.dam || null,
          pedigree_number: verificationResult.pedigree_number || null,
          inbreeding_coefficient: verificationResult.inbreeding_coefficient || null
        },
        verification_source: 'canecorsopedigree.com',
        verification_status: 'verified'
      });
      if (pedigreeError) {
        setVerificationError('Pedigree save failed: ' + pedigreeError.message);
        setSaving(false);
        return;
      }

      const trustScore = calculateTrustScore(verificationResult);
      await supabase.from('dogs').update({ trust_score: trustScore }).eq('id', dog.id);
      setDog(prev => ({ ...prev, trust_score: trustScore }));

      // Refresh pedigree rows from DB so the saved data shows immediately
      const { data: refreshed } = await supabase
        .from('pedigrees')
        .select('*')
        .eq('dog_id', dog.id)
        .order('created_at', { ascending: false });
      if (refreshed) setPedigreeRows(refreshed);

      setSaved(true);
    } catch (err) {
      setVerificationError('Save failed: ' + (err.message || 'Unknown error'));
    }
    setSaving(false);
  };

  const calculateTrustScore = (data) => {
    let score = 0;
    if (data.sire?.name) score += 15;
    if (data.dam?.name) score += 15;
    if (data.pedigree_number) score += 10;
    if (data.hd_score && data.hd_score !== 'unknown') score += 20;
    if (data.ed_score && data.ed_score !== 'Unknown' && data.ed_score !== 'unknown') score += 15;
    if (data.dsra_result && data.dsra_result !== 'UNKNOWN') score += 15;
    if (data.dvl2_result && data.dvl2_result !== 'UNKNOWN') score += 10;
    return Math.min(score, 100);
  };

  const startEditing = () => {
    setEditForm({
      call_name: dog.call_name || '',
      weight: dog.weight || '',
      height: dog.height || '',
      color: dog.color || '',
      stud_fee: dog.stud_fee || '',
      available_for_breeding: dog.available_for_breeding || false,
      dob: dog.dob || '',
      sex: dog.sex || '',
      registration_number: dog.registration_number || '',
    });
    setEditing(true);
  };

  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async () => {
    if (!dog || !user) return;
    setSavingEdit(true);
    try {
      const updates = {
        call_name: editForm.call_name || null,
        weight: editForm.weight ? Number(editForm.weight) : null,
        height: editForm.height ? Number(editForm.height) : null,
        color: editForm.color || null,
        stud_fee: editForm.stud_fee ? Number(editForm.stud_fee) : null,
        available_for_breeding: editForm.available_for_breeding,
        dob: editForm.dob || null,
        sex: editForm.sex || null,
        registration_number: editForm.registration_number || null,
        updated_at: new Date().toISOString(),
      };
      const { error } = await supabase
        .from('dogs')
        .update(updates)
        .eq('id', dog.id)
        .eq('owner_id', user.id);
      if (error) throw error;
      setDog(prev => ({ ...prev, ...updates }));
      setEditing(false);
    } catch (err) {
      console.error('Error saving dog edit:', err);
      alert('Failed to save changes: ' + (err.message || 'Unknown error'));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteDog = async () => {
    if (!dog || !user) return;
    setDeleting(true);
    try {
      // Delete photos from storage
      const { data: photos } = await supabase
        .from('dog_photos')
        .select('url')
        .eq('dog_id', dog.id);
      if (photos && photos.length > 0) {
        const filePaths = photos.map(p => {
          try {
            const url = new URL(p.url);
            return url.pathname.split('/object/public/dog-photos/')[1];
          } catch { return null; }
        }).filter(Boolean);
        if (filePaths.length > 0) {
          await supabase.storage.from('dog-photos').remove(filePaths);
        }
      }
      // Delete related records
      await supabase.from('dog_photos').delete().eq('dog_id', dog.id);
      await supabase.from('health_records').delete().eq('dog_id', dog.id);
      await supabase.from('pedigrees').delete().eq('dog_id', dog.id);
      // Delete the dog itself (owner_id check for safety)
      await supabase.from('dogs').delete().eq('id', dog.id).eq('owner_id', user.id);
      navigate('/dashboard/breeder');
    } catch (err) {
      console.error('Error deleting dog:', err);
      alert('Failed to delete dog. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !dog || !user) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPEG, PNG, WEBP, HEIC, or HEIF)');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploadingPhoto(true);
    setUploadingType('profile');

    try {
      const timestamp = Date.now();
      const filePath = `${dog.id}/profile-${timestamp}-${file.name}`;

      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('dog-photos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('dog-photos')
        .getPublicUrl(filePath);

      // Check if this is the first profile photo (for trust score bonus)
      const isFirstPhoto = !dog.profile_photo_url;

      // Update dogs table with profile photo URL
      const newTrustScore = isFirstPhoto ? Math.min((dog.trust_score || 0) + 5, 100) : dog.trust_score;
      const { error: updateError } = await supabase
        .from('dogs')
        .update({ 
          profile_photo_url: publicUrl,
          trust_score: newTrustScore
        })
        .eq('id', dog.id);

      if (updateError) throw updateError;

      // Also insert into dog_photos table as primary
      await supabase.from('dog_photos').insert({
        dog_id: dog.id,
        url: publicUrl,
        uploaded_by: user.id,
        is_primary: true,
        sort_order: 0
      });

      // Update local state
      setDog(prev => ({ ...prev, profile_photo_url: publicUrl, trust_score: newTrustScore }));
      
      if (isFirstPhoto) {
        alert('Profile photo uploaded! +5 Trust Score bonus added.');
      }
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
      setUploadingType(null);
      if (profilePhotoInputRef.current) {
        profilePhotoInputRef.current.value = '';
      }
    }
  };

  const handleGalleryPhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !dog || !user) return;

    setUploadingPhoto(true);
    setUploadingType('gallery');

    try {
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
      const maxSortOrder = dogPhotos.length > 0 
        ? Math.max(...dogPhotos.map(p => p.sort_order)) + 1 
        : 1;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Validate file type
        if (!validTypes.includes(file.type)) {
          console.warn(`Skipping ${file.name}: invalid file type`);
          continue;
        }

        // Validate file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
          console.warn(`Skipping ${file.name}: file too large`);
          continue;
        }

        const timestamp = Date.now();
        const filePath = `${dog.id}/gallery-${timestamp}-${file.name}`;

        // Upload to Supabase storage
        const { error: uploadError } = await supabase.storage
          .from('dog-photos')
          .upload(filePath, file);

        if (uploadError) {
          console.error(`Error uploading ${file.name}:`, uploadError);
          continue;
        }

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('dog-photos')
          .getPublicUrl(filePath);

        // Insert into dog_photos table
        const { data: newPhoto, error: insertError } = await supabase
          .from('dog_photos')
          .insert({
            dog_id: dog.id,
            url: publicUrl,
            uploaded_by: user.id,
            is_primary: false,
            sort_order: maxSortOrder + i
          })
          .select()
          .single();

        if (!insertError && newPhoto) {
          setDogPhotos(prev => [...prev, newPhoto]);
        }
      }
    } catch (error) {
      console.error('Error uploading gallery photos:', error);
      alert('Failed to upload some photos. Please try again.');
    } finally {
      setUploadingPhoto(false);
      setUploadingType(null);
      if (galleryPhotoInputRef.current) {
        galleryPhotoInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async (photo) => {
    if (!confirm('Are you sure you want to delete this photo?')) return;

    try {
      // Extract file path from URL
      const urlParts = photo.url.split('/dog-photos/');
      const filePath = urlParts[1];

      // Delete from storage
      if (filePath) {
        await supabase.storage
          .from('dog-photos')
          .remove([filePath]);
      }

      // Delete from dog_photos table
      await supabase
        .from('dog_photos')
        .delete()
        .eq('id', photo.id);

      // If it was the profile photo, clear it from dogs table
      if (photo.is_primary || photo.url === dog.profile_photo_url) {
        await supabase
          .from('dogs')
          .update({ profile_photo_url: null })
          .eq('id', dog.id);
        setDog(prev => ({ ...prev, profile_photo_url: null }));
      }

      // Update local state
      setDogPhotos(prev => prev.filter(p => p.id !== photo.id));
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Failed to delete photo. Please try again.');
    }
  };

  const isOwner = user && dog && user.id === dog.owner_id;

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#0A1628] py-12 px-6 flex items-center justify-center">
          <div className="text-white">Loading dog profile...</div>
        </div>
      </Layout>
    );
  }
  
  if (!dog) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#0A1628] py-12 px-6 flex items-center justify-center">
          <div className="text-white">Dog not found</div>
        </div>
      </Layout>
    );
  }
  
  const badge = getTrustScoreBadge(dog.trust_score);
  const healthTests = formatHealthTests();
  
  return (
    <Layout>
      <div className="min-h-screen bg-[#0A1628] py-12 px-6" data-testid="dog-profile-page">
        <div className="max-w-7xl mx-auto">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-slate-300 hover:text-white mb-6"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div>
              <div className="bg-[#1E3A5F]/40 backdrop-blur-md border border-white/10 rounded-xl overflow-hidden h-96 flex items-center justify-center relative group">
                {dog.profile_photo_url ? (
                  <>
                    <img
                      src={dog.profile_photo_url}
                      alt={dog.registered_name}
                      className="w-full h-full object-cover"
                      data-testid="dog-main-image"
                    />
                    {isOwner && (
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Button
                          onClick={() => profilePhotoInputRef.current?.click()}
                          disabled={uploadingPhoto}
                          className="bg-[#C5A55A] text-[#0A1628] hover:bg-[#D4B66A]"
                          data-testid="change-profile-photo-btn"
                        >
                          {uploadingPhoto && uploadingType === 'profile' ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Camera className="w-4 h-4 mr-2" />
                          )}
                          Change Photo
                        </Button>
                      </div>
                    )}
                  </>
                ) : isOwner ? (
                  <div className="text-center">
                    <Button
                      onClick={() => profilePhotoInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="bg-[#C5A55A] text-[#0A1628] hover:bg-[#D4B66A]"
                      data-testid="upload-profile-photo-btn"
                    >
                      {uploadingPhoto && uploadingType === 'profile' ? (
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      ) : (
                        <Camera className="w-5 h-5 mr-2" />
                      )}
                      Upload Profile Photo
                    </Button>
                    <p className="text-slate-400 text-sm mt-2">+5 Trust Score for first photo!</p>
                  </div>
                ) : (
                  <div className="text-[#C5A55A] text-6xl font-bold">
                    {dog.registered_name?.charAt(0) || '?'}
                  </div>
                )}
                <input
                  ref={profilePhotoInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.heic,.heif"
                  onChange={handleProfilePhotoUpload}
                  className="hidden"
                  data-testid="profile-photo-input"
                />
              </div>
            </div>
            
            <div>
              <h1 
                className="text-5xl font-bold text-white mb-3"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="dog-name"
              >
                {dog.call_name || dog.registered_name}
              </h1>
              <p className="text-2xl text-slate-400 mb-6">{dog.breed}</p>
              
              <div className="mb-6" data-testid="trust-score-section">
                <span className="text-slate-400 text-sm block mb-1">Trust Score</span>
                <div className="flex items-center gap-3">
                  <span 
                    className="text-4xl font-bold" 
                    style={{ color: '#C5A55A' }}
                    data-testid="trust-score-value"
                  >
                    {dog.trust_score !== null && dog.trust_score !== undefined ? dog.trust_score : 0}
                  </span>
                  {badge && (
                    <span
                      className="inline-block px-3 py-1 rounded-full text-sm font-semibold border"
                      style={{
                        color: badge.color,
                        borderColor: badge.color,
                        backgroundColor: badge.color + '20'
                      }}
                      data-testid="trust-score-badge"
                    >
                      {badge.text}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="bg-[#1E3A5F]/40 backdrop-blur-md border border-white/10 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">Details</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-[#C5A55A]" />
                    <div>
                      <span className="text-slate-400 text-sm">Age</span>
                      <p className="text-white font-medium">{calculateAge(dog.dob)}</p>
                    </div>
                  </div>
                  {dog.weight && (
                    <div className="flex items-center gap-3">
                      <Weight className="w-5 h-5 text-[#C5A55A]" />
                      <div>
                        <span className="text-slate-400 text-sm">Weight</span>
                        <p className="text-white font-medium">{dog.weight} lbs</p>
                      </div>
                    </div>
                  )}
                  {dog.registration_number && (
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-[#C5A55A]" />
                      <div>
                        <span className="text-slate-400 text-sm">Registration</span>
                        <p className="text-white font-medium">{dog.registration_number}</p>
                      </div>
                    </div>
                  )}
                  {dog.sex && (
                    <div className="flex items-center gap-3">
                      <span className="text-slate-400 text-sm">Sex:</span>
                      <p className="text-white font-medium">{dog.sex}</p>
                    </div>
                  )}
                  {dog.color && (
                    <div className="flex items-center gap-3">
                      <span className="text-slate-400 text-sm">Color:</span>
                      <p className="text-white font-medium">{dog.color}</p>
                    </div>
                  )}
                  {dog.height && (
                    <div className="flex items-center gap-3">
                      <span className="text-slate-400 text-sm">Height:</span>
                      <p className="text-white font-medium">{dog.height} in</p>
                    </div>
                  )}
                  {dog.stud_fee && (
                    <div className="flex items-center gap-3">
                      <span className="text-slate-400 text-sm">Stud Fee:</span>
                      <p className="text-white font-medium">${Number(dog.stud_fee).toLocaleString()}</p>
                    </div>
                  )}
                  {dog.available_for_breeding && (
                    <div className="mt-2">
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-400 border border-green-500/30">
                        ✓ Available for Breeding
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              {owner && (
                <div className="bg-[#1E3A5F]/40 backdrop-blur-md border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Breeder</h3>
                  <div
                    onClick={() => navigate(`/breeder/${owner.id}`)}
                    className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                    data-testid="breeder-link"
                  >
                    <div className="w-12 h-12 rounded-full bg-[#C5A55A] flex items-center justify-center text-[#0A1628] font-bold text-xl">
                      {owner.kennel_name?.charAt(0) || owner.full_name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="text-white font-medium">{owner.kennel_name || owner.full_name}</p>
                      <p className="text-sm text-slate-400">{owner.location}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit Dog Button - Owner Only */}
              {isOwner && !editing && (
                <button
                  onClick={startEditing}
                  className="w-full mt-4 py-3 px-4 rounded-lg bg-[#C5A55A] text-[#0A1628] font-semibold hover:bg-[#D4B66A] transition-colors text-sm"
                >
                  ✏️ Edit Dog Profile
                </button>
              )}

              {/* Edit Form - Owner Only */}
              {isOwner && editing && (
                <div className="mt-4 bg-[#1E3A5F]/40 backdrop-blur-md border border-[#C5A55A]/30 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Edit Profile</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Call Name</label>
                      <input type="text" value={editForm.call_name} onChange={(e) => handleEditChange('call_name', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0A1628] border border-white/10 text-white text-sm focus:border-[#C5A55A] outline-none" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-slate-400 text-xs block mb-1">Weight (lbs)</label>
                        <input type="number" value={editForm.weight} onChange={(e) => handleEditChange('weight', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0A1628] border border-white/10 text-white text-sm focus:border-[#C5A55A] outline-none" />
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs block mb-1">Height (in)</label>
                        <input type="number" value={editForm.height} onChange={(e) => handleEditChange('height', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0A1628] border border-white/10 text-white text-sm focus:border-[#C5A55A] outline-none" />
                      </div>
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Color</label>
                      <input type="text" value={editForm.color} onChange={(e) => handleEditChange('color', e.target.value)} placeholder="e.g. Black, Blue, Fawn" className="w-full px-3 py-2 rounded-lg bg-[#0A1628] border border-white/10 text-white text-sm focus:border-[#C5A55A] outline-none" />
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Sex</label>
                      <select value={editForm.sex} onChange={(e) => handleEditChange('sex', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0A1628] border border-white/10 text-white text-sm focus:border-[#C5A55A] outline-none">
                        <option value="">Select</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Date of Birth</label>
                      <input type="date" value={editForm.dob} onChange={(e) => handleEditChange('dob', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0A1628] border border-white/10 text-white text-sm focus:border-[#C5A55A] outline-none" />
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Registration Number</label>
                      <input type="text" value={editForm.registration_number} onChange={(e) => handleEditChange('registration_number', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#0A1628] border border-white/10 text-white text-sm focus:border-[#C5A55A] outline-none" />
                    </div>
                    <div>
                      <label className="text-slate-400 text-xs block mb-1">Stud Fee ($)</label>
                      <input type="number" value={editForm.stud_fee} onChange={(e) => handleEditChange('stud_fee', e.target.value)} placeholder="e.g. 2500" className="w-full px-3 py-2 rounded-lg bg-[#0A1628] border border-white/10 text-white text-sm focus:border-[#C5A55A] outline-none" />
                    </div>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" checked={editForm.available_for_breeding} onChange={(e) => handleEditChange('available_for_breeding', e.target.checked)} className="w-4 h-4 accent-[#C5A55A]" />
                      <label className="text-slate-300 text-sm">Available for Breeding</label>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button onClick={handleSaveEdit} disabled={savingEdit} className="flex-1 py-2 px-4 rounded-lg bg-[#C5A55A] text-[#0A1628] font-semibold hover:bg-[#D4B66A] disabled:opacity-50 transition-colors text-sm">
                        {savingEdit ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button onClick={() => setEditing(false)} className="flex-1 py-2 px-4 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 text-sm transition-colors">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="mb-8">
            <h2 
              className="text-3xl font-bold text-white mb-6 flex items-center gap-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <Award className="w-8 h-8" />
              Health Test Results
            </h2>

            {user?.id === dog.owner_id && (
              <div style={{ margin: '24px 0', padding: '20px', background: '#1a1a2e', borderRadius: '12px', border: '1px solid #2d2d44' }}>
                {pedigreeRows && pedigreeRows.length > 0 && (
                  <div style={{ background: 'rgba(74,222,128,0.1)', border: '1px solid #4ade80', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                    <h4 style={{ color: '#4ade80', marginBottom: '12px' }}>✅ Verified Pedigree</h4>
                    {pedigreeRows.map((row, i) => (
                      <div key={i} style={{ marginBottom: '8px', color: '#fff' }}>
                        <strong style={{ textTransform: 'capitalize' }}>{row.position}:</strong> {row.ancestor_name} {row.ancestor_registration ? '(' + row.ancestor_registration + ')' : ''}
                      </div>
                    ))}
                  </div>
                )}
                {/* Verify pedigree input section */}
                {(!pedigreeRows || pedigreeRows.length === 0) && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                      <div>
                        <h3 style={{ color: '#c9a94e', margin: '0 0 4px 0', fontSize: '18px' }}>Pedigree Verification</h3>
                        <p style={{ color: '#999', margin: '0 0 12px 0', fontSize: '14px' }}>
                          {verificationResult ? 'Verification complete — data pulled from canecorsopedigree.com' : 'Enter your dog\'s canecorsopedigree.com ID to pull verified health records and pedigree data.'}
                        </p>
                      </div>
                      {!verificationResult && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                          <input
                            type="text"
                            value={pedigreeId}
                            onChange={(e) => setPedigreeId(e.target.value)}
                            placeholder="e.g. 115752"
                            style={{
                              padding: '10px 14px',
                              background: '#0d0d1a',
                              border: '1px solid #2d2d44',
                              borderRadius: '8px',
                              color: '#fff',
                              fontSize: '14px',
                              width: '160px'
                            }}
                          />
                          <button
                            onClick={handleVerifyPedigree}
                            disabled={verifying}
                            style={{
                              padding: '10px 24px',
                              background: verifying ? '#555' : 'linear-gradient(135deg, #c9a94e, #b8962d)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: verifying ? 'not-allowed' : 'pointer',
                              fontWeight: 'bold',
                              fontSize: '14px'
                            }}
                          >
                            {verifying ? 'Verifying...' : '🔍 Verify Pedigree'}
                          </button>
                        </div>
                      )}
                    </div>
                    {verificationError && (
                      <p style={{ color: '#ff6b6b', marginTop: '12px', fontSize: '14px' }}>{verificationError}</p>
                    )}
                    {verificationResult && (
                      <div style={{ marginTop: '16px', padding: '16px', background: '#0d0d1a', borderRadius: '8px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                          <div><span style={{ color: '#999', fontSize: '12px' }}>Registered Name</span><p style={{ color: '#fff', margin: '4px 0 0' }}>{verificationResult.registered_name}</p></div>
                          <div><span style={{ color: '#999', fontSize: '12px' }}>Sire</span><p style={{ color: '#fff', margin: '4px 0 0' }}>{verificationResult.sire?.name || 'Unknown'}</p></div>
                          <div><span style={{ color: '#999', fontSize: '12px' }}>Dam</span><p style={{ color: '#fff', margin: '4px 0 0' }}>{verificationResult.dam?.name || 'Unknown'}</p></div>
                          <div><span style={{ color: '#999', fontSize: '12px' }}>HD (Hips)</span><p style={{ color: verificationResult.hd_score ? '#4ade80' : '#999', margin: '4px 0 0', fontWeight: 'bold' }}>{verificationResult.hd_score || 'Not tested'}</p></div>
                          <div><span style={{ color: '#999', fontSize: '12px' }}>ED (Elbows)</span><p style={{ color: verificationResult.ed_score ? '#4ade80' : '#999', margin: '4px 0 0', fontWeight: 'bold' }}>{verificationResult.ed_score || 'Not tested'}</p></div>
                          <div><span style={{ color: '#999', fontSize: '12px' }}>DSRA</span><p style={{ color: verificationResult.dsra_certified ? '#4ade80' : '#999', margin: '4px 0 0', fontWeight: 'bold' }}>{verificationResult.dsra_result || 'Not tested'}</p></div>
                          <div><span style={{ color: '#999', fontSize: '12px' }}>Color</span><p style={{ color: '#fff', margin: '4px 0 0' }}>{verificationResult.color || 'Unknown'}</p></div>
                          <div><span style={{ color: '#999', fontSize: '12px' }}>Inbreeding</span><p style={{ color: '#fff', margin: '4px 0 0' }}>{verificationResult.inbreeding_coefficient ? verificationResult.inbreeding_coefficient.toFixed(2) + '%' : 'Unknown'}</p></div>
                        </div>
                        <p style={{ color: '#4ade80', marginTop: '16px', fontSize: '13px' }}>✅ Verified from canecorsopedigree.com at {new Date(verificationResult.verified_at).toLocaleString()}</p>
                        {!saved ? (
                          <button
                            onClick={handleSaveVerification}
                            disabled={saving}
                            style={{
                              marginTop: '16px',
                              padding: '10px 24px',
                              background: saving ? '#555' : 'linear-gradient(135deg, #4ade80, #22c55e)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: saving ? 'not-allowed' : 'pointer',
                              fontWeight: 'bold',
                              fontSize: '14px'
                            }}
                          >
                            {saving ? 'Saving...' : '💾 Save to Profile'}
                          </button>
                        ) : (
                          <p style={{ color: '#4ade80', marginTop: '16px', fontWeight: 'bold' }}>✅ Verified data saved to profile!</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {healthTests.map((test, index) => (
                <HealthTestCard key={index} test={test} />
              ))}
            </div>
          </div>

          {/* Photo Gallery Section */}
          <div className="mb-8" data-testid="photo-gallery-section">
            <h2 
              className="text-3xl font-bold text-white mb-6 flex items-center gap-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <Image className="w-8 h-8" />
              Photos
              {dogPhotos.length > 0 && (
                <span className="ml-2 px-2 py-1 bg-[#C5A55A]/20 text-[#C5A55A] text-sm rounded-full">
                  {dogPhotos.length}
                </span>
              )}
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {dogPhotos.filter(p => !p.is_primary).map((photo) => (
                <div 
                  key={photo.id} 
                  className="relative group aspect-square bg-[#1E3A5F]/40 backdrop-blur-md border border-white/10 rounded-xl overflow-hidden"
                  data-testid={`gallery-photo-${photo.id}`}
                >
                  <img
                    src={photo.url}
                    alt={photo.caption || 'Gallery photo'}
                    className="w-full h-full object-cover"
                  />
                  {isOwner && (
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button
                        onClick={() => handleDeletePhoto(photo)}
                        variant="destructive"
                        size="sm"
                        className="bg-red-500 hover:bg-red-600"
                        data-testid={`delete-photo-${photo.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  {photo.caption && (
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                      <p className="text-white text-xs truncate">{photo.caption}</p>
                    </div>
                  )}
                </div>
              ))}

              {/* Add Photos Button (Owner Only) */}
              {isOwner && (
                <div 
                  className="aspect-square bg-[#1E3A5F]/20 border-2 border-dashed border-white/20 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-[#C5A55A]/50 hover:bg-[#1E3A5F]/30 transition-all"
                  onClick={() => !uploadingPhoto && galleryPhotoInputRef.current?.click()}
                  data-testid="add-gallery-photos-btn"
                >
                  {uploadingPhoto && uploadingType === 'gallery' ? (
                    <Loader2 className="w-8 h-8 text-[#C5A55A] animate-spin" />
                  ) : (
                    <>
                      <Plus className="w-8 h-8 text-[#C5A55A] mb-2" />
                      <span className="text-slate-400 text-sm">Add Photos</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {dogPhotos.filter(p => !p.is_primary).length === 0 && !isOwner && (
              <div className="text-center py-12 bg-[#1E3A5F]/20 rounded-xl border border-white/10">
                <Image className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                <p className="text-slate-400">No photos yet</p>
              </div>
            )}

            <input
              ref={galleryPhotoInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.heic,.heif"
              multiple
              onChange={handleGalleryPhotoUpload}
              className="hidden"
              data-testid="gallery-photo-input"
            />
          </div>
          
          {pedigreeRows.length > 0 && (
            <div data-testid="pedigree-section">
              <h2 
                className="text-3xl font-bold text-white mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Pedigree
              </h2>
              <div className="bg-[#1E3A5F]/40 backdrop-blur-md border border-white/10 rounded-xl p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-1 flex items-center justify-center">
                    <div className="text-center bg-[#0A1628] rounded-xl p-6 w-full border border-[#C5A55A]/50">
                      <div className="w-20 h-20 rounded-full bg-[#C5A55A] flex items-center justify-center text-[#0A1628] font-bold text-2xl mx-auto mb-3">
                        {dog.registered_name?.charAt(0) || '?'}
                      </div>
                      <h3 className="font-bold text-white text-lg mb-1">{dog.registered_name}</h3>
                      <p className="text-xs text-slate-400">{dog.registration_number}</p>
                    </div>
                  </div>
                  
                  <div className="md:col-span-2 space-y-4">
                    {pedigreeRows.map((row, idx) => {
                      const isSire = row.position?.toLowerCase() === 'sire';
                      const isDam = row.position?.toLowerCase() === 'dam';
                      return (
                        <div 
                          key={row.id || idx} 
                          className="bg-[#0A1628] rounded-xl p-6 border border-white/10" 
                          data-testid={`pedigree-${row.position || 'ancestor'}-${idx}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-3 h-3 rounded-full ${isSire ? 'bg-[#3498DB]' : isDam ? 'bg-[#E91E63]' : 'bg-[#C5A55A]'}`}></div>
                            <span className="text-sm text-slate-400 uppercase tracking-wider">
                              {row.position || 'Ancestor'}
                            </span>
                            {row.source && (
                              <span className="text-xs text-green-400 ml-2">✓ Verified</span>
                            )}
                          </div>
                          <h4 className="font-semibold text-white text-lg mb-1">{row.ancestor_name}</h4>
                          {row.ancestor_registration && (
                            <p className="text-xs text-slate-400">{row.ancestor_registration}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div className="mt-6 text-center">
                  <p className="text-sm text-slate-400">
                    3-generation pedigree available upon request
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Delete Dog Section - Owner Only */}
          {isOwner && (
            <div className="mt-8 pt-6 border-t border-slate-700">
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full py-3 px-4 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors text-sm"
                >
                  Delete This Dog
                </button>
              ) : (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <p className="text-red-400 text-sm font-semibold mb-2">Are you sure?</p>
                  <p className="text-slate-400 text-xs mb-4">This will permanently delete this dog profile, photos, health records, and pedigree data. This cannot be undone.</p>
                  <div className="flex gap-3">
                    <button
                      onClick={handleDeleteDog}
                      disabled={deleting}
                      className="flex-1 py-2 px-4 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50 transition-colors"
                    >
                      {deleting ? 'Deleting...' : 'Yes, Delete Forever'}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1 py-2 px-4 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 text-sm transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default DogProfile;
